import React, { useEffect, useState } from 'react';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/joy';
import Box from '@mui/joy/Box';
import Checkbox from '@mui/joy/Checkbox';
import IconButton from '@mui/joy/IconButton';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';

import { User } from '../services/types/api.generated';

export interface TableColumn<T extends { id: string | number }, K extends keyof T> {
  header: string;
  key: K;
  width?: number | string;
  render?: (value: T[K], row: T) => React.ReactNode;
}

export interface DynamicTableProps<T extends { id: string | number }, K extends keyof T> {
  columns: Array<TableColumn<T, K>>;
  data: Array<T>;
  selectedRows: T[];
  onSelectionChange?: (selectedRows: T[]) => void;
  currentUser?: User;
  checkbox?: boolean;
  onRowClick?: (row: T) => void;
  selectable?: boolean;
}

const DynamicTable = <T extends { id: string | number }, K extends keyof T>({
  columns,
  data,
  selectedRows,
  onSelectionChange,
  currentUser,
  checkbox = false,
  onRowClick,
  selectable = true
}: DynamicTableProps<T, K>): React.ReactElement => {
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const generateKey = (row: T): string => {
    // Always use the id as the primary key if available
    if (row.id) {
      return `id-${row.id}`;
    }
    // Fallback to using column values if id is not available
    const keyPart1 = row[columns[0]?.key] || 'missing';
    const keyPart2 = row[columns[1]?.key] || 'fallback';
    return `${keyPart1}-${keyPart2}`;
  };

  useEffect(() => {
    setSelected(selectedRows.map((row) => generateKey(row)));
  }, [selectedRows]);

  const mapKeysToRows = (keys: readonly string[]): T[] =>
    keys.map((key) => data.find((row) => generateKey(row) === key)!);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = event.target.checked
      ? data.filter((row) => !(currentUser && row.id === currentUser.id.toString())).map((n) => generateKey(n))
      : [];

    setSelected(newSelected);
    if (onSelectionChange) {
      onSelectionChange(mapKeysToRows(newSelected));
    }
  };

  const handleClick = (event: React.MouseEvent<unknown>, key: string, row: T) => {
    if (onRowClick && !checkbox) {
      onRowClick(row);
      return;
    }
    // Otherwise, handle checkbox selection
    if (currentUser && row.id === currentUser.id.toString()) {
      return;
    }

    const selectedIndex = selected.indexOf(key);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, key);
    } else {
      newSelected = selected.filter((selectedKey) => selectedKey !== key);
    }

    setSelected(newSelected);

    if (onSelectionChange) {
      onSelectionChange(mapKeysToRows(newSelected).filter(Boolean));
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any, newValue: number | null) => {
    setRowsPerPage(parseInt(newValue!.toString(), 10));
    setPage(0);
  };

  const isSelected = (key: string) => selected.indexOf(key) !== -1;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const getLabelDisplayedRowsTo = () => {
    if (data.length === -1) {
      return (page + 1) * rowsPerPage;
    }
    return rowsPerPage === -1 ? data.length : Math.min(data.length, (page + 1) * rowsPerPage);
  };

  const theme = useTheme();

  return (
    <>
      <Table aria-label='dynamic table'>
        <thead>
          <tr>
            {checkbox && (
              <th style={{ width: '3%' }}>
                <Checkbox
                  checked={
                    data.filter((row) => !(currentUser && row.id === currentUser.id.toString())).length > 0 &&
                    selected.length ===
                      data.filter((row) => !(currentUser && row.id === currentUser.id.toString())).length
                  }
                  indeterminate={
                    selected.length > 0 &&
                    selected.length <
                      data.filter((row) => !(currentUser && row.id === currentUser.id.toString())).length
                  }
                  onChange={handleSelectAllClick}
                />
              </th>
            )}
            {columns.map((column, colIndex) => (
              <th
                key={`header-${column.key as string}-${colIndex}`}
                style={{ padding: '8px', textAlign: 'left', width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => {
            const key = generateKey(row);
            const isItemSelected = isSelected(key);
            const isCurrentUser = currentUser ? row.id === currentUser.id.toString() : false;

            return (
              <tr
                key={key}
                onClick={selectable ? (event) => handleClick(event, key, row) : undefined}
                style={{
                  backgroundColor: isItemSelected
                    ? theme.palette.primary.softActiveBg
                    : rowIndex % 2 === 1
                    ? theme.palette.background.level1
                    : 'inherit',
                  cursor: onRowClick && !checkbox ? 'pointer' : 'default'
                }}
              >
                {checkbox && (
                  <td>
                    <Checkbox checked={isItemSelected} />
                  </td>
                )}
                {columns.map((column, colIndex) => (
                  <td key={`${key}-${column.key as string}-${colIndex}`} style={{ padding: '8px' }}>
                    {column.render ? column.render(row[column.key], row) : (row[column.key] as string)}
                    {isCurrentUser && column.key === 'name' && (
                      <span
                        className='rounded-full ml-1 px-3 py-1'
                        style={{
                          backgroundColor: theme.palette.success[100],
                          color: theme.palette.success[700]
                        }}
                      >
                        You
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
          {emptyRows > 0 && (
            <tr style={{ height: 53 * emptyRows }}>
              <td aria-hidden='true' colSpan={columns.length + 1} />
            </tr>
          )}
        </tbody>
      </Table>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'flex-end',
          mt: 2
        }}
      >
        <Select onChange={handleChangeRowsPerPage} value={rowsPerPage}>
          <Option value={10}>10</Option>
          <Option value={25}>25</Option>
        </Select>
        <Typography sx={{ textAlign: 'center', minWidth: 80 }}>
          {`${page * rowsPerPage + 1}-${getLabelDisplayedRowsTo()} of ${data.length}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton disabled={page === 0} onClick={() => handleChangePage(page - 1)} size='sm'>
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton
            disabled={data.length !== -1 ? page >= Math.ceil(data.length / rowsPerPage) - 1 : false}
            onClick={() => handleChangePage(page + 1)}
            size='sm'
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

export default DynamicTable;
